import { type Maybe, type Serializable } from "isntnt";
import type React from "react";
import { type ForwardRefExoticComponent, type FunctionComponent } from "react";
import { GlobalContextType } from "../../utils/GlobalContext.ts";
import { PageContentFragment } from "../../graphql/graphql.ts";
import { EntryIdentifier } from "../../types/EntryIdentifier.ts";
import { deepOmitUndefined } from "./deepOmitUndefined.ts";
import {
  ComponentContentPropsTransformMap,
  propsTransformMap,
} from "../../components/utils/ComponentMapping/ComponentTransformersMap.ts";

type ExtractDataType<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends () => Promise<ComponentPropsTransformer<any, any>>,
> = T extends () => Promise<infer Transformer>
  ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Transformer extends ComponentPropsTransformer<any, infer Props>
    ? Props
    : never
  : never;

export type ComponentContentProps = {
  [Type in keyof ComponentContentPropsTransformMap]: {
    id: string;
    type: Type;
    data: ExtractDataType<ComponentContentPropsTransformMap[Type]>;
    error?: string;
  };
}[keyof ComponentContentPropsTransformMap];

export type TransformedComponentProps = {
  content: Array<ComponentContentProps>;
};

export type ComponentPropsTransformContext = GlobalContextType;

type ComponentPropsTransform<Type extends string, Props> = (
  identifier: EntryIdentifier<Type>,
  context: ComponentPropsTransformContext,
) => Promise<Props>;

export type ComponentPropsTransformer<Type extends string, Props> = {
  component:
    | FunctionComponent<Props>
    | ForwardRefExoticComponent<Props>
    | React.ComponentType<Props>;
  transform: ComponentPropsTransform<Type, Props>;
};

export async function componentTransformer(
  data: PageContentFragment,
  context: GlobalContextType,
): Promise<TransformedComponentProps> {
  const content = await transformContentProps(
    [data.hero, ...(data.blocksCollection?.items ?? [])].filter(Boolean),
    context,
  );

  return {
    content: content.filter(
      (componentContent) => componentContent !== null,
    ) as Array<ComponentContentProps>,
  };
}

export async function transformContentProps(
  identifiers: Array<Maybe<EntryIdentifier>>,
  context: ComponentPropsTransformContext,
): Promise<Array<ComponentContentProps | null>> {
  const promises = identifiers.map(async (identifier) => {
    if (!identifier) {
      return null;
    }

    const { __typename: type, sys } = identifier;

    const { id } = sys ?? {};

    if (!sys) {
      console.warn("No identifier fragment found for type:", type);
      return {
        id,
        type,
        data: null,
        error: `No identifier fragment found for type: ${type}`,
      };
    }

    if (!Object.hasOwn(propsTransformMap, type)) {
      console.warn("No transformer found for type:", type);
      return {
        id,
        type,
        data: null,
        error: `No transformer found for type: ${type}`,
      };
    }

    const transformerImportFunction =
      propsTransformMap[type as keyof typeof propsTransformMap];

    try {
      const transformer = await transformerImportFunction();

      const transformedData = await transformer.transform(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        identifier as EntryIdentifier<any>,
        context,
      );

      const data = deepOmitUndefined(
        transformedData as unknown as Serializable,
      ) as unknown as typeof transformedData;

      return {
        id,
        type,
        data,
      } as ComponentContentProps;
    } catch (error) {
      console.error("Error in transformer for type:", type, error);
      return null;
    }
  });

  const results = await Promise.all(promises);

  return results.filter((item): item is ComponentContentProps => item !== null);
}

export function createPropsTransformer<Type extends string, Props>(
  component:
    | FunctionComponent<Props>
    | ForwardRefExoticComponent<Props>
    | React.ComponentType<Props>,
  transform: ComponentPropsTransform<Type, Props>,
): ComponentPropsTransformer<Type, Props> {
  return {
    component,
    transform,
  };
}
